import { useEffect, useRef, useState } from 'react';
import {
  canCreateProjects,
  canEditAtLevel,
  inOrg,
} from '@shared-lib/org-access';
import { projectService } from '../services/projectService';
import {
  getStructuresByProjectId,
  getRoomCountsForStructures,
} from '../services/structureService';
import { authService } from '../services/authService';
import { useLoading } from '../contexts/LoadingContext';
import { useChat } from '../contexts/ChatContext';

/**
 * A design project as a page reads it (moved out of ProjectDetail for the
 * books desk's phase 3b): the project, its scans and room counts, who the
 * signed-in user is to it, and the chat assistant's context. Used by
 * ProjectDetail and by the contractor's job page.
 *
 * `loading` is the FIRST read; `reloading` is a later one (after a rename,
 * a reprocess, a photo) — ProjectDetail blanks for both, as it always has,
 * while the job page keeps showing what it has so its books session is not
 * torn down by a design refresh. `overlay` drives the app's full-screen
 * loading overlay during a read (ProjectDetail's behaviour).
 *
 * @returns {{ project, structures, roomCounts, constructionDocs,
 *   setConstructionDocs, userAccessLevel, canEditProject, mayCreateProjects,
 *   inProjectOrg, loading, reloading, error, reload }}
 */
export default function useProjectDesign(projectId, { overlay = false } = {}) {
  const [project, setProject] = useState(null);
  const [structures, setStructures] = useState([]);
  // structureId -> room count, from a count aggregation (no documents).
  const [roomCounts, setRoomCounts] = useState({});
  const [constructionDocs, setConstructionDocs] = useState([]);
  // 'owner' | 'editor' | 'viewer' | 'none' once resolved; null until then, so
  // neither the edit controls nor the admin banner show for a moment early.
  const [userAccessLevel, setUserAccessLevel] = useState(null);
  // May make a copy (a copy is a new project): inside an organization only
  // owner and office members create projects — field staff are assigned to
  // them instead (canCreateProjects). False until the claims are read.
  const [mayCreateProjects, setMayCreateProjects] = useState(false);
  // a member of the project's organization (field crew included), which is
  // what opens it as a job for a contractor (books/jobPageModel opensAsJob)
  const [inProjectOrg, setInProjectOrg] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState(null);
  const loadedRef = useRef(false);
  const user = authService.getCurrentUser();
  const { showLoading, hideLoading } = useLoading();
  const { updateContext } = useChat();

  // Clean up context when component unmounts
  useEffect(() => {
    return () => {
      // Only clear structures if we're not navigating to a structure in this project
      // Check if the next location is a structure view
      const nextPath = window.location.pathname;
      const isNavigatingToStructure =
        nextPath.includes('/structure/') ||
        nextPath.includes('/pretransformed-floorplan/') ||
        nextPath.includes('/elevations/');

      if (!isNavigatingToStructure) {
        // Clear structures only if not going to a structure view
        updateContext({
          cache: {
            structures: [], // Clear structures when truly leaving project context
            rooms: [], // Clear rooms
            checkpoints: [], // Clear checkpoints
          },
        });
      } else {
        // Just clear rooms and checkpoints, preserve structures
        updateContext({
          cache: {
            // structures preserved
            rooms: [], // Clear rooms
            checkpoints: [], // Clear checkpoints
          },
        });
      }
    };
  }, [updateContext]);

  const reload = async () => {
    const first = !loadedRef.current;
    try {
      if (first) setLoading(true);
      else setReloading(true);
      if (overlay) showLoading();

      // Load project details
      const projectData = await projectService.getProject(projectId);
      setProject(projectData);

      // Check user's access level. The claims come from the ID token; a
      // failed token read (an offline refresh of an expired token) must not
      // fail the page the project doc already loaded for — it fails CLOSED to
      // no organization, which reads as 'none' for a member and changes
      // nothing for an owner or shared viewer, as useProjectAccess does.
      if (user && projectData) {
        let claims = {};
        try {
          claims = await authService.getTokenClaims();
        } catch (claimsError) {
          console.error('Could not read the token claims:', claimsError);
        }
        setUserAccessLevel(
          projectService.getUserAccessLevel(projectData, user.uid, claims)
        );
        setMayCreateProjects(canCreateProjects(claims));
        setInProjectOrg(inOrg(claims, projectData));
      }

      // Load construction documents if they exist
      if (
        projectData.constructionDocs &&
        Array.isArray(projectData.constructionDocs)
      ) {
        setConstructionDocs(projectData.constructionDocs);
      }

      // ONE query, not two. `getStructuresByProjectId` and
      // `getPreTransformedStructuresByProjectId` are the same query against the
      // same collection — `structures`, `projectId ==`, `createdAt desc` — so
      // running both downloaded every structure doc twice (avg 27 KB, up to
      // 270 KB each). They differ only in spread order (`{id, ...data}` vs
      // `{...data, id}`), and no structure doc carries an `id` field that
      // disagrees with its doc id, so the results are identical.
      const projectStructures = await getStructuresByProjectId(projectId);
      const preTransformedStructs = projectStructures;

      setStructures(projectStructures);

      // Update context with project location AND loaded structures together.
      // Built from ONE list — concatenating both copies listed every scan twice.
      const structuresForContext = projectStructures.map((s) => ({
        id: s.id,
        name: s.name || s.originalData?.name || 'Unnamed Structure',
        projectId,
        roomCount: s.roomCount || 0,
      }));

      // Update both location and cache together
      updateContext({
        location: {
          projectId,
          projectName: projectData.name,
          structureId: null,
          structureName: null,
          roomId: null,
          roomName: null,
          path: window.location.pathname,
        },
        cache: {
          structures: structuresForContext,
          rooms: [], // Clear rooms when viewing project
          checkpoints: [], // Clear checkpoints
        },
      });

      // NOTE: this page deliberately does NOT fetch room documents on load.
      //
      // It used to fetch them TWICE — once into a `rooms` state nothing ever
      // read, and once into `preTransformedRooms` — pulling complete room docs
      // (objects / walls / ceilings) for every scan in the project. On
      // `819 Montrose Ave` that was 63 documents / 2,952 KB, of which the page
      // rendered 5.4 KB: a name, a thumbnail, and a count.
      //
      // The count now comes from a server-side aggregation that transfers no
      // documents, and the room DOCUMENTS are loaded lazily by the effect
      // below — only if the user actually switches to the Rooms view.
      if (preTransformedStructs.length > 0) {
        const structureIds = preTransformedStructs.map((s) => s.id);
        setRoomCounts(await getRoomCountsForStructures(structureIds));
      }
    } catch (err) {
      console.error('Error loading project data:', err);
      setError('Failed to load project. Please try again later.');
    } finally {
      loadedRef.current = true;
      setLoading(false);
      setReloading(false);
      if (overlay) hideLoading();
    }
  };

  useEffect(() => {
    loadedRef.current = false;
    reload();
  }, [projectId]);

  return {
    project,
    structures,
    roomCounts,
    constructionDocs,
    setConstructionDocs,
    userAccessLevel,
    canEditProject: canEditAtLevel(userAccessLevel),
    mayCreateProjects,
    inProjectOrg,
    loading,
    reloading,
    error,
    reload,
  };
}
